import { instance } from "api/apiBase";
import { normalizeFilename, normalizeFile } from "shared/utils";

interface FileInfoType {
  dataURL?: string | null;
  name: string;
  size: number;
  type: string;
}

function addNameToDataURL(dataURL: string, name: string) {
  if (!dataURL) return null;

  return dataURL.replace(";base64", `;name=${encodeURIComponent(name)};base64`);
}

export function processFile(file: File): Promise<FileInfoType> {
  const { name, size, type } = file;
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onerror = reject;
    reader.onload = (event) => {
      resolve({
        dataURL:
          typeof event.target?.result === "string"
            ? addNameToDataURL(event.target.result, name)
            : null,
        name,
        size,
        type
      });
    };
    reader.readAsDataURL(file);
  });
}

export function svgToBlob(svgContent: string) {
  return new Blob([svgContent], { type: "image/svg+xml" });
}

export function svgToPngBlob(svgContent: string): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const svgBlob = svgToBlob(svgContent);
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d", { alpha: true });

      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob);
          else resolve(svgBlob);
        }, "image/png");
      } else {
        URL.revokeObjectURL(url);
        resolve(svgBlob);
      }
    };

    img.src = url;
  });
}

export const getPresignedUrl = async (file: File) => {
  const queryParams = new URLSearchParams({
    filename: normalizeFilename(file.name),
    type: file.type
  }).toString();

  const { data } = await instance(`/portal/v1/assets/presign?${queryParams}`);
  return data.url;
};

export const uploadFileToS3Bucket = (s3Url: string, fileObj: File) => {
  return fetch(s3Url, {
    method: "PUT",
    body: normalizeFile(fileObj),
    mode: "cors"
  });
};

export const dataURLtoFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

export const createAssetRecord = async (url: string, file: File) => {
  try {
    const { data } = await instance.post("/portal/v1/assets", {
      url,
      metadata: {
        original_filename: normalizeFilename(file.name),
        content_type: file.type,
        size: file.size
      }
    });
    return data.asset.global_id;
  } catch (error) {
    console.error("Error creating asset record:", error);
    throw error;
  }
};
